import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URLS from '../config/api';

const AuthContext = createContext();

// Constants for localStorage keys
const AUTH_TOKEN_KEY = 'authToken';
const USER_DATA_KEY = 'userData';

// Configure axios to include token in all requests
const setupAxiosDefaults = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Setup response interceptor for handling 401 errors
let interceptorId = null;
const setupResponseInterceptor = (onAuthError) => {
  // Remove existing interceptor if any
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }
  
  // Add new interceptor
  interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Only handle 401 errors for non-validation requests
      if (error.response?.status === 401 && !error.config._isValidationRequest) {
        onAuthError();
      }
      return Promise.reject(error);
    }
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Function to clear all auth data
  const clearAuth = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    setupAxiosDefaults(null);
    setUser(null);
    setToken(null);
  }, []);

  // Function to handle 401 errors (but not during initialization)
  const handleAuthError = useCallback(() => {
    // Don't handle auth errors during initialization
    if (!isInitialized) {
      console.log('⚠️ Ignoring auth error during initialization');
      return;
    }
    
    console.log('⚠️ Authentication error detected, clearing auth...');
    clearAuth();
    // Only redirect if not already on login page
    if (window.location.pathname !== '/login' && window.location.pathname !== '/admin-signup') {
      window.location.href = '/login';
    }
  }, [clearAuth, isInitialized]);

  const initializeAuth = useCallback(async () => {
    console.log('🔍 Initializing authentication from localStorage...');
    
    try {
      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_DATA_KEY);
      
      console.log('📦 Stored token exists:', !!storedToken);
      console.log('📦 Stored user exists:', !!storedUser);
      
      if (storedToken && storedUser) {
        // Parse stored user data
        let userData;
        try {
          userData = JSON.parse(storedUser);
        } catch (e) {
          console.error('Failed to parse stored user data:', e);
          clearAuth();
          setLoading(false);
          setIsInitialized(true);
          return;
        }
        
        // IMMEDIATELY set the user and token from localStorage
        // This prevents redirect while we validate
        setUser(userData);
        setToken(storedToken);
        setupAxiosDefaults(storedToken);
        
        console.log('✅ Loaded user from localStorage:', userData);
        console.log('✅ User role:', userData.role);
        
        // IMPORTANT: Set loading to false BEFORE async validation
        // This ensures the user sees the page immediately
        setLoading(false);
        setIsInitialized(true);
        
        // Then try to validate with backend in the background (optional)
        // This validation should NOT block the UI or cause redirects
        if (API_URLS.CURRENT_USER) {
          try {
            const response = await axios.get(API_URLS.CURRENT_USER, {
              headers: { 'Authorization': `Bearer ${storedToken}` },
              _isValidationRequest: true,
              timeout: 5000 // 5 second timeout
            });
            
            // Only update user data if we get valid response with user data
            if (response.data?.user) {
              const serverUserData = response.data.user;
              setUser(serverUserData);
              localStorage.setItem(USER_DATA_KEY, JSON.stringify(serverUserData));
              console.log('✅ User data refreshed from server:', serverUserData);
            } else if (response.data?.isAuthenticated === false) {
              // ONLY clear if server EXPLICITLY says not authenticated
              // AND we got a proper response (not just missing fields)
              console.log('⚠️ Server says not authenticated, but keeping local auth for now');
              // Don't clear immediately - let the 401 interceptor handle real auth failures
            }
          } catch (validationError) {
            // Don't clear auth on validation errors - keep the stored auth
            // The 401 interceptor will handle real authentication failures
            console.log('⚠️ Background validation failed (keeping local auth):', 
              validationError.response?.status || validationError.message
            );
          }
        }
        
        return; // Exit early - loading is already false
      }
      
      // No stored auth data
      console.log('ℹ️ No stored authentication found');
    } catch (error) {
      console.error('❌ Auth initialization error:', error);
      // Don't clear auth on initialization errors
    }
    
    setLoading(false);
    setIsInitialized(true);
  }, [clearAuth]);

  useEffect(() => {
    // Initialize auth on mount
    initializeAuth();
    
    // Setup response interceptor after a delay to avoid catching init requests
    const timer = setTimeout(() => {
      setupResponseInterceptor(handleAuthError);
    }, 1000); // Increased delay to ensure init completes
    
    // Cleanup on unmount
    return () => {
      clearTimeout(timer);
      if (interceptorId !== null) {
        axios.interceptors.response.eject(interceptorId);
      }
    };
  }, [initializeAuth, handleAuthError]);

  const checkAuth = async () => {
    // For backward compatibility - re-runs initialization
    await initializeAuth();
  };

  const login = async (username, password) => {
    try {
      console.log('🔐 Attempting login...');
      
      const response = await axios.post(API_URLS.LOGIN, { 
        username, 
        password 
      });

      // Handle different response formats from backend
      const success = response.data.status === 'success' || 
                     response.data.success === true ||
                     (response.data.user && !response.data.error);
      
      if (success) {
        // Extract token from various possible fields
        const authToken = response.data.token || 
                         response.data.access_token || 
                         response.data.authToken ||
                         response.data.key; // Some Django REST frameworks use 'key'
        
        const userData = response.data.user || {
          id: response.data.id,
          username: response.data.username || username,
          role: response.data.role || response.data.user_type || 'user',
          email: response.data.email
        };
        
        // If no token in response, backend might be using sessions
        // In that case, use a session marker
        const tokenToStore = authToken || `session-${Date.now()}`;
        
        // Store auth data in localStorage
        localStorage.setItem(AUTH_TOKEN_KEY, tokenToStore);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        
        // Set up axios with the token
        if (authToken) {
          setupAxiosDefaults(authToken);
        }
        
        // Update state
        setToken(tokenToStore);
        setUser(userData);
        setIsInitialized(true);
        
        console.log('✅ Login successful:', userData);
        
        return {
          success: true,
          user: userData,
          token: tokenToStore
        };
      } else {
        const errorMessage = response.data.message || response.data.detail || 'Login failed';
        console.error('❌ Login failed:', errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      
      // Extract error message from various possible locations
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.response?.data?.detail ||
                          error.response?.data?.non_field_errors?.[0] ||
                          error.message || 
                          'An error occurred during login';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      console.log('👋 Logging out...');
      
      // Try to call logout endpoint if it exists
      if (token && API_URLS.LOGOUT) {
        try {
          await axios.post(API_URLS.LOGOUT, {});
        } catch (error) {
          // Continue with logout even if API call fails
          console.error('Logout API call failed (continuing with local logout):', error);
        }
      }
      
      // Clear local storage and state
      clearAuth();
      
      console.log('✅ Logout successful');
      
      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Still clear auth even if logout API fails
      clearAuth();
      
      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  };

  const signup = async (userData, isOfficer = false) => {
    try {
      console.log('📝 Attempting signup...');
      
      const signupUrl = isOfficer ? API_URLS.OFFICER_SIGNUP : API_URLS.ADMIN_SIGNUP;
      
      const response = await axios.post(signupUrl, userData);

      // Handle different response formats
      const success = response.data.status === 'success' || 
                     response.data.success === true ||
                     (response.data.user && !response.data.error);
      
      if (success) {
        // Extract token from various possible fields
        const authToken = response.data.token || 
                         response.data.access_token || 
                         response.data.authToken ||
                         response.data.key;
        
        const userInfo = response.data.user || {
          id: response.data.id,
          username: response.data.username || userData.username,
          role: response.data.role || response.data.user_type || (isOfficer ? 'officer' : 'admin'),
          email: response.data.email || userData.email
        };
        
        // If no token in response, backend might be using sessions
        const tokenToStore = authToken || `session-${Date.now()}`;
        
        // Store auth data in localStorage
        localStorage.setItem(AUTH_TOKEN_KEY, tokenToStore);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userInfo));
        
        // Set up axios with the token
        if (authToken) {
          setupAxiosDefaults(authToken);
        }
        
        // Update state
        setToken(tokenToStore);
        setUser(userInfo);
        setIsInitialized(true);
        
        console.log('✅ Signup successful:', userInfo);
        
        return {
          success: true,
          user: userInfo,
          token: tokenToStore
        };
      } else {
        const errorMessage = response.data.message || response.data.detail || 'Signup failed';
        console.error('❌ Signup failed:', errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (error) {
      console.error('❌ Signup error:', error);
      
      // Extract error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.response?.data?.detail ||
                          error.response?.data?.non_field_errors?.[0] ||
                          error.message || 
                          'An error occurred during signup';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const updateUser = (updatedUserData) => {
    // Update user data in state and localStorage
    setUser(updatedUserData);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(updatedUserData));
    console.log('✅ User data updated:', updatedUserData);
  };

  const refreshToken = async () => {
    try {
      console.log('🔄 Refreshing token...');
      
      // Only attempt if we have a refresh token endpoint
      if (!API_URLS.REFRESH_TOKEN) {
        console.log('ℹ️ No refresh token endpoint configured');
        return false;
      }
      
      const response = await axios.post(API_URLS.REFRESH_TOKEN);
      
      if (response.data.token || response.data.access_token) {
        const newToken = response.data.token || response.data.access_token;
        
        // Update token in localStorage and axios
        localStorage.setItem(AUTH_TOKEN_KEY, newToken);
        setupAxiosDefaults(newToken);
        setToken(newToken);
        
        console.log('✅ Token refreshed successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      return false;
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    signup,
    loading,
    checkAuth,
    updateUser,
    refreshToken,
    isAuthenticated: !!user && !!token,
    isInitialized,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;



// import React, { createContext, useState, useEffect } from 'react';
// import axios from 'axios';
// import API_URLS from '../config/api';

// const AuthContext = createContext();

// // Constants for localStorage keys
// const AUTH_TOKEN_KEY = 'authToken';
// const USER_DATA_KEY = 'userData';

// // Configure axios to include token in all requests
// const setupAxiosDefaults = (token) => {
//   if (token) {
//     axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
//   } else {
//     delete axios.defaults.headers.common['Authorization'];
//   }
// };

// // Setup response interceptor for handling 401 errors
// let interceptorId = null;
// const setupResponseInterceptor = (onAuthError) => {
//   // Remove existing interceptor if any
//   if (interceptorId !== null) {
//     axios.interceptors.response.eject(interceptorId);
//   }
  
//   // Add new interceptor
//   interceptorId = axios.interceptors.response.use(
//     (response) => response,
//     (error) => {
//       // Only handle 401 errors for non-validation requests
//       if (error.response?.status === 401 && !error.config._isValidationRequest) {
//         onAuthError();
//       }
//       return Promise.reject(error);
//     }
//   );
// };

// export const AuthProvider = ({ children }) => {
//   const [user, setUser] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [token, setToken] = useState(null);

//   // Function to clear all auth data
//   const clearAuth = () => {
//     localStorage.removeItem(AUTH_TOKEN_KEY);
//     localStorage.removeItem(USER_DATA_KEY);
//     setupAxiosDefaults(null);
//     setUser(null);
//     setToken(null);
//   };

//   // Function to handle 401 errors (but not during initialization)
//   const handleAuthError = () => {
//     console.log('⚠️ Authentication error detected, clearing auth...');
//     clearAuth();
//     // Only redirect if not already on login page
//     if (window.location.pathname !== '/login' && window.location.pathname !== '/admin-signup') {
//       window.location.href = '/login';
//     }
//   };

//   useEffect(() => {
//     // Initialize auth on mount
//     initializeAuth();
    
//     // Setup response interceptor after initialization
//     const timer = setTimeout(() => {
//       setupResponseInterceptor(handleAuthError);
//     }, 100);
    
//     // Cleanup on unmount
//     return () => {
//       clearTimeout(timer);
//       if (interceptorId !== null) {
//         axios.interceptors.response.eject(interceptorId);
//       }
//     };
//   }, []);

//   const initializeAuth = async () => {
//     console.log('🔍 Initializing authentication from localStorage...');
    
//     try {
//       const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
//       const storedUser = localStorage.getItem(USER_DATA_KEY);
      
//       if (storedToken && storedUser) {
//         // Parse stored user data
//         let userData;
//         try {
//           userData = JSON.parse(storedUser);
//         } catch (e) {
//           console.error('Failed to parse stored user data:', e);
//           clearAuth();
//           setLoading(false);
//           return;
//         }
        
//         // First, set the user and token from localStorage
//         // This prevents redirect while we validate
//         setUser(userData);
//         setToken(storedToken);
//         setupAxiosDefaults(storedToken);
        
//         console.log('✅ Loaded user from localStorage:', userData);
        
//         // Then try to validate with backend (optional validation)
//         try {
//           // Create a custom axios instance for validation to avoid interceptor
//           const validationConfig = {
//             ...API_URLS.CURRENT_USER && { 
//               headers: { 
//                 'Authorization': `Bearer ${storedToken}` 
//               },
//               _isValidationRequest: true // Mark this as a validation request
//             }
//           };
          
//           // Only validate if we have a CURRENT_USER endpoint
//           if (API_URLS.CURRENT_USER) {
//             const response = await axios.get(API_URLS.CURRENT_USER, validationConfig);
            
//             if (response.data.isAuthenticated && response.data.user) {
//               // Token is valid, update user data from server
//               const serverUserData = response.data.user;
//               setUser(serverUserData);
//               localStorage.setItem(USER_DATA_KEY, JSON.stringify(serverUserData));
//               console.log('✅ Token validated with server, user updated:', serverUserData);
//             } else if (response.data.isAuthenticated === false) {
//               // Explicitly not authenticated
//               console.log('❌ Server says user not authenticated, clearing auth');
//               clearAuth();
//             }
//             // If response doesn't have expected format, keep the stored auth
//           } else {
//             console.log('ℹ️ No CURRENT_USER endpoint configured, skipping validation');
//           }
//         } catch (validationError) {
//           // Validation failed, but we'll keep the stored auth unless it's a 401
//           if (validationError.response?.status === 401) {
//             console.log('❌ Token rejected by server (401), clearing auth');
//             clearAuth();
//           } else {
//             // Network error or other issue - keep the stored auth
//             console.log('⚠️ Could not validate token with server, keeping stored auth:', validationError.message);
//             // Keep the user logged in with stored data
//           }
//         }
//       } else {
//         // No stored auth data
//         console.log('ℹ️ No stored authentication found');
//       }
//     } catch (error) {
//       console.error('❌ Auth initialization error:', error);
//       // Don't clear auth on initialization errors
//     } finally {
//       setLoading(false);
//     }
//   };

//   const checkAuth = async () => {
//     // For backward compatibility - re-runs initialization
//     await initializeAuth();
//   };

//   const login = async (username, password) => {
//     try {
//       console.log('🔐 Attempting login...');
      
//       const response = await axios.post(API_URLS.LOGIN, { 
//         username, 
//         password 
//       });

//       // Handle different response formats from backend
//       const success = response.data.status === 'success' || 
//                      response.data.success === true ||
//                      (response.data.user && !response.data.error);
      
//       if (success) {
//         // Extract token from various possible fields
//         const authToken = response.data.token || 
//                          response.data.access_token || 
//                          response.data.authToken ||
//                          response.data.key; // Some Django REST frameworks use 'key'
        
//         const userData = response.data.user || {
//           id: response.data.id,
//           username: response.data.username || username,
//           role: response.data.role || response.data.user_type || 'user',
//           email: response.data.email
//         };
        
//         // If no token in response, backend might be using sessions
//         // In that case, use a session marker
//         const tokenToStore = authToken || `session-${Date.now()}`;
        
//         // Store auth data in localStorage
//         localStorage.setItem(AUTH_TOKEN_KEY, tokenToStore);
//         localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        
//         // Set up axios with the token
//         if (authToken) {
//           setupAxiosDefaults(authToken);
//         }
        
//         // Update state
//         setToken(tokenToStore);
//         setUser(userData);
        
//         console.log('✅ Login successful:', userData);
        
//         return {
//           success: true,
//           user: userData,
//           token: tokenToStore
//         };
//       } else {
//         const errorMessage = response.data.message || response.data.detail || 'Login failed';
//         console.error('❌ Login failed:', errorMessage);
//         return {
//           success: false,
//           message: errorMessage
//         };
//       }
//     } catch (error) {
//       console.error('❌ Login error:', error);
      
//       // Extract error message from various possible locations
//       const errorMessage = error.response?.data?.message || 
//                           error.response?.data?.error || 
//                           error.response?.data?.detail ||
//                           error.response?.data?.non_field_errors?.[0] ||
//                           error.message || 
//                           'An error occurred during login';
      
//       return {
//         success: false,
//         message: errorMessage
//       };
//     }
//   };

//   const logout = async () => {
//     try {
//       console.log('👋 Logging out...');
      
//       // Try to call logout endpoint if it exists
//       if (token && API_URLS.LOGOUT) {
//         try {
//           await axios.post(API_URLS.LOGOUT, {});
//         } catch (error) {
//           // Continue with logout even if API call fails
//           console.error('Logout API call failed (continuing with local logout):', error);
//         }
//       }
      
//       // Clear local storage and state
//       clearAuth();
      
//       console.log('✅ Logout successful');
      
//       // Redirect to login page
//       if (window.location.pathname !== '/login') {
//         window.location.href = '/login';
//       }
//     } catch (error) {
//       console.error('❌ Logout error:', error);
//       // Still clear auth even if logout API fails
//       clearAuth();
      
//       // Redirect to login page
//       if (window.location.pathname !== '/login') {
//         window.location.href = '/login';
//       }
//     }
//   };

//   const signup = async (userData, isOfficer = false) => {
//     try {
//       console.log('📝 Attempting signup...');
      
//       const signupUrl = isOfficer ? API_URLS.OFFICER_SIGNUP : API_URLS.ADMIN_SIGNUP;
      
//       const response = await axios.post(signupUrl, userData);

//       // Handle different response formats
//       const success = response.data.status === 'success' || 
//                      response.data.success === true ||
//                      (response.data.user && !response.data.error);
      
//       if (success) {
//         // Extract token from various possible fields
//         const authToken = response.data.token || 
//                          response.data.access_token || 
//                          response.data.authToken ||
//                          response.data.key;
        
//         const userInfo = response.data.user || {
//           id: response.data.id,
//           username: response.data.username || userData.username,
//           role: response.data.role || response.data.user_type || (isOfficer ? 'officer' : 'admin'),
//           email: response.data.email || userData.email
//         };
        
//         // If no token in response, backend might be using sessions
//         const tokenToStore = authToken || `session-${Date.now()}`;
        
//         // Store auth data in localStorage
//         localStorage.setItem(AUTH_TOKEN_KEY, tokenToStore);
//         localStorage.setItem(USER_DATA_KEY, JSON.stringify(userInfo));
        
//         // Set up axios with the token
//         if (authToken) {
//           setupAxiosDefaults(authToken);
//         }
        
//         // Update state
//         setToken(tokenToStore);
//         setUser(userInfo);
        
//         console.log('✅ Signup successful:', userInfo);
        
//         return {
//           success: true,
//           user: userInfo,
//           token: tokenToStore
//         };
//       } else {
//         const errorMessage = response.data.message || response.data.detail || 'Signup failed';
//         console.error('❌ Signup failed:', errorMessage);
//         return {
//           success: false,
//           message: errorMessage
//         };
//       }
//     } catch (error) {
//       console.error('❌ Signup error:', error);
      
//       // Extract error message
//       const errorMessage = error.response?.data?.message || 
//                           error.response?.data?.error || 
//                           error.response?.data?.detail ||
//                           error.response?.data?.non_field_errors?.[0] ||
//                           error.message || 
//                           'An error occurred during signup';
      
//       return {
//         success: false,
//         message: errorMessage
//       };
//     }
//   };

//   const updateUser = (updatedUserData) => {
//     // Update user data in state and localStorage
//     setUser(updatedUserData);
//     localStorage.setItem(USER_DATA_KEY, JSON.stringify(updatedUserData));
//     console.log('✅ User data updated:', updatedUserData);
//   };

//   const refreshToken = async () => {
//     try {
//       console.log('🔄 Refreshing token...');
      
//       // Only attempt if we have a refresh token endpoint
//       if (!API_URLS.REFRESH_TOKEN) {
//         console.log('ℹ️ No refresh token endpoint configured');
//         return false;
//       }
      
//       const response = await axios.post(API_URLS.REFRESH_TOKEN);
      
//       if (response.data.token || response.data.access_token) {
//         const newToken = response.data.token || response.data.access_token;
        
//         // Update token in localStorage and axios
//         localStorage.setItem(AUTH_TOKEN_KEY, newToken);
//         setupAxiosDefaults(newToken);
//         setToken(newToken);
        
//         console.log('✅ Token refreshed successfully');
//         return true;
//       }
      
//       return false;
//     } catch (error) {
//       console.error('❌ Token refresh failed:', error);
//       return false;
//     }
//   };

//   const value = {
//     user,
//     token,
//     login,
//     logout,
//     signup,
//     loading,
//     checkAuth,
//     updateUser,
//     refreshToken,
//     isAuthenticated: !!user && !!token,
//   };

//   return (
//     <AuthContext.Provider value={value}>
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export default AuthContext;
